/** Stable codes for schedule chain / assign failures (Admin i18n). */

export type ScheduleValidationCode =
  | 'ARRIVE_AFTER_LATEST'
  | 'STANDBY_REQUIRES_END'
  | 'STANDBY_MUST_START'
  | 'STANDBY_CONFLICT'
  | 'JOB_NOT_IN_PLAN'
  | 'FIXED_CANNOT_REASSIGN'
  | 'VEHICLE_NOT_FOUND'
  | 'CAPACITY_SHORT'
  | 'FLEET_MISMATCH';

export interface ScheduleValidationIssue {
  code: ScheduleValidationCode;
  params: Record<string, string | number>;
}

function minutesLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function formatMinutesLabel(minutes: number): string {
  return minutesLabel(minutes);
}

/** English fallback for logs / tests; UI should prefer i18n on `code`. */
export function formatIssueEnglish(issue: ScheduleValidationIssue): string {
  const p = issue.params;
  switch (issue.code) {
    case 'ARRIVE_AFTER_LATEST':
      return `${p.leg}: arrives ${p.arrive} after latest start ${p.latest}`;
    case 'STANDBY_REQUIRES_END':
      return `${p.leg}: Stand by block requires end time`;
    case 'STANDBY_MUST_START':
      return `${p.leg}: must start Stand by by ${p.time}`;
    case 'STANDBY_CONFLICT':
      return `${p.leg}: conflicts with Stand by block ${p.block} (${p.start}–${p.end})`;
    case 'JOB_NOT_IN_PLAN':
      return `Job ${p.jobId} not found in plan catalog`;
    case 'FIXED_CANNOT_REASSIGN':
      return `Job ${p.jobId} is a published fixed block and cannot be reassigned`;
    case 'VEHICLE_NOT_FOUND':
      return `Vehicle ${p.vehicleId} not found`;
    case 'CAPACITY_SHORT':
      return `Vehicle ${p.vehicle} capacity ${p.capacity} < ${p.passengers} passengers`;
    case 'FLEET_MISMATCH':
      return `Vehicle ${p.vehicle} is not in the job's preferred fleet`;
    default: {
      const _exhaustive: never = issue.code;
      return String(_exhaustive);
    }
  }
}

export function formatIssuesEnglish(issues: ScheduleValidationIssue[]): string[] {
  return [...new Set(issues.map(formatIssueEnglish))];
}
